import { useRouter } from "next/router";
import React, { useMemo, useRef, useState } from "react";

import type {
  EditTravellerPayload,
  EndorsementComponentData,
} from "@tm/api-utils";
import { submitEndorsement } from "@tm/insurance/api";
import {
  FormattedText,
  ImageComponent,
  LandingHeaderMobile,
  ReactPortalComp,
  Spinner,
} from "@tm/static";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { theme } from "@tm/utility/global";
import CommonModalDweb from "../../../../components/CommonModalDweb/CommonModalDweb";
import { ORDER_DETAIL_V2_ASSETS } from "../../constants";
import type {
  EditTravellersModalProps,
  EndorsementView,
  Entity,
  FormConfig,
  FormDataItem,
} from "../../types";
import {
  convertYYYYMMDDToDDMMYYYY,
  FormCard,
  FormRendererFields,
  mapSubjectErrorsToFieldErrors,
  normalizeFormConfig,
  useFormRenderer,
} from "../FormRenderer";
import { InfoModalContent } from "../InfoModalContent";
import {
  EditTravellersSheetStyle,
  ModalFooter,
  ModalFooterActions,
  SaveButton,
  SheetContent,
  StickyErrorBanner,
  StickyErrorIcon,
  StickyErrorText,
  SubmitModalWrapper,
  TravellerFormSection,
} from "./EditTravellersModal.style";

// ── Entity data extractor for traveller form ────────────────────────────

const extractTravellerData = (entity: Entity): Record<string, unknown> =>
  entity.data;

// ── Payload builder — only includes fields that actually changed ────────

const buildEditTravellerPayload = (
  entities: Entity[],
  formData: FormDataItem[],
  initialData: FormDataItem[],
  formConfig: FormConfig,
  bookingId: string
): { payload: EditTravellerPayload } | null => {
  const subjects: Array<{
    subject_id: string;
    new_pax_data: Record<string, unknown>;
  }> = [];

  entities.forEach((entity, index) => {
    const current = formData[index];
    const initial = initialData[index];
    if (!current || !initial) return;

    const changedFields: Record<string, unknown> = {};

    formConfig.fields.forEach((field) => {
      const key = field.name;
      const curVal = String(current[key] || "").trim();
      const initVal = String(initial[key] || "").trim();

      if (field.isMasked && curVal === initVal) return;
      if (curVal === initVal) return;

      let value = String(current[key] || "");
      if (field.type === "DATE" && value) {
        value = convertYYYYMMDDToDDMMYYYY(value);
      }
      changedFields[key] = value;
    });

    formConfig.dynamicFields?.forEach((df) => {
      const curDyn = current.dynamicFields?.[df.triggerId];
      const initDyn = initial.dynamicFields?.[df.triggerId];

      if (curDyn?.visible) {
        df.fields.forEach((field) => {
          const curVal = String(curDyn[field.name] || "").trim();
          const initVal = String(initDyn?.[field.name] || "").trim();
          if (curVal !== initVal || curDyn.visible !== initDyn?.visible) {
            changedFields[field.name] = curDyn[field.name] || "";
          }
        });
      } else if (initDyn?.visible && !curDyn?.visible) {
        df.fields.forEach((field) => {
          if (String(initDyn[field.name] || "").trim()) {
            changedFields[field.name] = "";
          }
        });
      }
    });

    if (Object.keys(changedFields).length > 0) {
      subjects.push({
        subject_id: entity.id,
        new_pax_data: changedFields,
      });
    }
  });

  if (subjects.length === 0) return null;

  return { payload: { booking_id: bookingId, subjects } };
};

// ── Component ───────────────────────────────────────────────────────────

export const EditTravellersModal: React.FC<EditTravellersModalProps> = ({
  isOpen,
  onClose,
  formConfig: rawFormConfig,
  entities,
  bookingId,
  onSave,
  onEndorsementSuccess,
  modalTitle = "Edit Travellers",
  saveButtonLabel = "SAVE DETAILS",
}) => {
  const device = useBreakpointV2();
  const router = useRouter();

  // Normalize fields that may arrive as either arrays or maps from the API
  const formConfig = useMemo(
    () => normalizeFormConfig(rawFormConfig),
    [rawFormConfig]
  );

  // ── Shared form state ─────────────────────────────────────────────────

  const {
    formData,
    errors,
    initialDataRef,
    getFieldRef,
    handleMaskedFocus,
    handleMaskedBlur,
    handleFieldChange,
    handleDynamicFieldChange,
    toggleDynamicField,
    validateAllFields,
    hasFormDataChanged,
    setErrors,
    scrollToFirstError,
  } = useFormRenderer({
    isOpen,
    formConfig,
    entities,
    extractEntityData: extractTravellerData,
  });

  // ── Endorsement state (modal-specific) ────────────────────────────────

  const [isLoading, setIsLoading] = useState(false);
  const [endorsementView, setEndorsementView] =
    useState<EndorsementView>("form");
  const [endorsementData, setEndorsementData] =
    useState<EndorsementComponentData | null>(null);
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null);
  const pendingRefreshRef = useRef(false);

  // Reset endorsement state when modal opens
  const prevIsOpenRef = useRef(false);
  React.useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setEndorsementView("form");
      setEndorsementData(null);
      setApiErrorMessage(null);
      setIsLoading(false);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  // Clear API error when user edits a field
  const wrappedHandleFieldChange = (
    entityIndex: number,
    fieldKey: string,
    value: string
  ) => {
    if (apiErrorMessage) setApiErrorMessage(null);
    handleFieldChange(entityIndex, fieldKey, value);
  };

  const wrappedHandleDynamicFieldChange = (
    entityIndex: number,
    triggerId: string,
    fieldKey: string,
    value: string
  ) => {
    if (apiErrorMessage) setApiErrorMessage(null);
    handleDynamicFieldChange(entityIndex, triggerId, fieldKey, value);
  };

  // ── Save / Close ──────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!validateAllFields()) return;

    const result = buildEditTravellerPayload(
      entities,
      formData,
      initialDataRef.current,
      formConfig,
      bookingId
    );

    if (!result) return;

    const { payload } = result;

    setIsLoading(true);
    try {
      const res = await submitEndorsement({
        bookingId,
        payload,
        query: router.query,
      });
      const componentData = res?.data?.components?.[0]?.data ?? null;

      setEndorsementData(componentData);

      if (res?.success) {
        setEndorsementView("success");
        setApiErrorMessage(null);
        initialDataRef.current = structuredClone(formData);
        pendingRefreshRef.current = true;
      } else {
        const subjectErrors = res?.data?.subjectErrors;

        if (subjectErrors && Object.keys(subjectErrors).length > 0) {
          const fieldErrors = mapSubjectErrorsToFieldErrors(
            subjectErrors,
            entities,
            formConfig
          );

          if (fieldErrors) {
            setErrors(fieldErrors);
            setApiErrorMessage(null);
            setTimeout(() => scrollToFirstError(fieldErrors), 100);
          } else {
            setApiErrorMessage(
              res?.message || "An error occurred. Please try again."
            );
          }
        } else {
          setApiErrorMessage(
            res?.message || "An error occurred. Please try again."
          );
        }

        setEndorsementView("form");
      }
    } catch {
      setApiErrorMessage("An error occurred. Please try again.");
      setEndorsementView("form");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (pendingRefreshRef.current) {
      pendingRefreshRef.current = false;
      onEndorsementSuccess?.();
    }
    onClose();
  };

  // ── Render ────────────────────────────────────────────────────────────

  if (!isOpen) return null;

  const isResultView = endorsementView === "success";
  const formChanged = hasFormDataChanged();

  const renderFormContent = () => (
    <>
      <TravellerFormSection>
        {formData.map((entityData, entityIndex) => (
          <FormCard key={entityIndex}>
            <FormRendererFields
              entityIndex={entityIndex}
              entityData={entityData}
              entityErrors={errors[entityIndex] || {}}
              formConfig={formConfig}
              getFieldRef={getFieldRef}
              handleFieldChange={wrappedHandleFieldChange}
              handleMaskedFocus={handleMaskedFocus}
              handleMaskedBlur={handleMaskedBlur}
              handleDynamicFieldChange={wrappedHandleDynamicFieldChange}
              toggleDynamicField={toggleDynamicField}
              setErrors={setErrors}
            />
          </FormCard>
        ))}
      </TravellerFormSection>

      <ModalFooter>
        {apiErrorMessage && (
          <StickyErrorBanner>
            <StickyErrorIcon>
              <ImageComponent
                src={ORDER_DETAIL_V2_ASSETS.VALIDATION_ERROR_ICON}
                width="24"
                height="24"
                alt="Error"
              />
            </StickyErrorIcon>
            <StickyErrorText>
              <FormattedText value={{ html: apiErrorMessage }} />
            </StickyErrorText>
          </StickyErrorBanner>
        )}
        <ModalFooterActions>
          <SaveButton
            onClick={handleSave}
            hasChanges={formChanged}
            isLoading={isLoading}
            disabled={!formChanged || isLoading}
          >
            {isLoading ? (
              <>
                <Spinner
                  spinnerWidth={16}
                  spinnerColor={theme.whiteText}
                  thickness={2}
                />
                <FormattedText value={saveButtonLabel} />
              </>
            ) : (
              <FormattedText value={saveButtonLabel} />
            )}
          </SaveButton>
        </ModalFooterActions>
      </ModalFooter>
    </>
  );

  const renderResultView = () => (
    <SubmitModalWrapper>
      <InfoModalContent
        viewType={endorsementView}
        data={endorsementData}
        renderAction={(data) => {
          if (data.cta?.isVisible !== false && data.newPolicyUrl) {
            return (
              <SaveButton
                as="a"
                href={data.newPolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
                hasChanges={true}
                isLoading={false}
                style={{ marginTop: "16px", textDecoration: "none" }}
              >
                <FormattedText value={data.cta?.label} />
              </SaveButton>
            );
          }
          return null;
        }}
      />
    </SubmitModalWrapper>
  );

  // Mobile: full-page sheet
  if (device === "sm") {
    return (
      <ReactPortalComp>
        <EditTravellersSheetStyle>
          <LandingHeaderMobile
            displayWhiteHeader
            headerTitle={isResultView ? "" : modalTitle}
            handleBackBtn={handleClose}
          />
          <SheetContent>
            {isResultView ? renderResultView() : renderFormContent()}
          </SheetContent>
        </EditTravellersSheetStyle>
      </ReactPortalComp>
    );
  }

  // Desktop: modal
  return (
    <CommonModalDweb
      customTitle={isResultView ? "" : modalTitle}
      handleClick={handleClose}
      customWidth={isResultView ? "420px" : "80%"}
      customHeight={isResultView ? "auto" : "640px"}
    >
      {isResultView ? renderResultView() : renderFormContent()}
    </CommonModalDweb>
  );
};
